import type { PickupLocation } from '@otw/models'

/** Default cap so Autocomplete does not mount thousands of list items. */
export const PICKUP_LOCATION_OPTION_LIMIT = 50

/** Single-line address summary for grids and detail headers. */
export const formatPickupLocationAddress = (
  location: PickupLocation | undefined | null,
): string => {
  if (!location) return ''
  const line1 = location.address?.trim() ?? ''
  const cityPart = [location.city, location.state]
    .map((p) => p?.trim())
    .filter(Boolean)
    .join(', ')
  const tail = [cityPart, location.zip?.trim()].filter(Boolean).join(' ')
  return [line1, tail].filter(Boolean).join(' • ')
}

/** Company or contact name only — pair with {@link formatPickupLocationAddress}. */
export const getPickupLocationName = (
  location: PickupLocation | undefined | null,
): string =>
  location?.company_name?.trim() || location?.contact_name?.trim() || ''

/** Contact-first label for vehicle / shop pickers (falls back to company). */
export const getPickupLocationContactLabel = (
  location: PickupLocation | undefined | null,
): string =>
  location?.contact_name?.trim() ||
  location?.company_name?.trim() ||
  'Unknown Contact'

/** Autocomplete `getOptionLabel` including address for search/display. */
export const getPickupLocationSearchLabel = (
  option: PickupLocation | string,
): string => {
  if (typeof option === 'string') return option
  const label = getPickupLocationContactLabel(option)
  const address = formatPickupLocationAddress(option)
  return address ? `${label} - ${address}` : label
}

const pickupLocationSearchHaystack = (location: PickupLocation): string =>
  [
    location.contact_name,
    location.company_name,
    location.address,
    location.city,
    location.state,
    location.zip,
    location.contact_phone,
  ]
    .map(part => part?.trim())
    .filter(Boolean)
    .join(' ')
    .toLowerCase()

/**
 * Filter saved pickup locations by contact, company, or address fields.
 * Caps results so large `pickup_locations` lists do not freeze Autocomplete.
 */
export const filterPickupLocationOptions = <T extends PickupLocation | string>(
  options: readonly T[],
  inputValue: string,
  opts?: { limit?: number },
): T[] => {
  const limit = opts?.limit ?? PICKUP_LOCATION_OPTION_LIMIT
  const q = inputValue.trim().toLowerCase()
  if (!q) {
    return options.slice(0, limit) as T[]
  }
  const filtered = options.filter(option => {
    if (typeof option === 'string') {
      return option.toLowerCase().includes(q)
    }
    return pickupLocationSearchHaystack(option).includes(q)
  })
  return filtered.slice(0, limit) as T[]
}

export const getPickupLocationLabel = (
  location: PickupLocation,
  options?: { terminalLabel?: string },
): string => {
  const addressPart = formatPickupLocationAddress(location)
  const terminalPrefix = options?.terminalLabel?.trim()
  if (terminalPrefix) {
    return addressPart
      ? `Terminal — ${terminalPrefix} — ${addressPart}`
      : `Terminal — ${terminalPrefix}`
  }
  if (location.company_name?.trim()) {
    return addressPart
      ? `${location.company_name.trim()} — ${addressPart}`
      : location.company_name.trim()
  }
  if (location.contact_name?.trim()) {
    return addressPart
      ? `${location.contact_name.trim()} — ${addressPart}`
      : location.contact_name.trim()
  }
  return addressPart || 'Pickup location'
}

export const resolvePickupLocationById = (
  locations: PickupLocation[] | undefined,
  locationId: string | null | undefined,
): PickupLocation | undefined => {
  const id = locationId?.trim()
  if (!id || !locations?.length) return undefined
  return locations.find((loc) => loc.id === id)
}

export const formatPickupLocationAddressesByIds = (
  locations: PickupLocation[] | undefined,
  locationIds: string[],
): string[] =>
  locationIds
    .map((id) => formatPickupLocationAddress(resolvePickupLocationById(locations, id)))
    .filter((label) => label.length > 0)

/** Multi-line delivery address: street on first line, "City, ST ZIP" on second. */
export const formatDeliveryAddressBlock = (
  location: PickupLocation | undefined | null,
): string => {
  if (!location) return 'Not assigned';
  const street = location.address?.trim() ?? '';
  const cityState = [location.city, location.state]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(', ');
  const cityStateZip = [cityState, location.zip?.trim()].filter(Boolean).join(' ');
  const lines = [street, cityStateZip].filter(Boolean);
  return lines.length > 0 ? lines.join('\n') : 'Not assigned';
}
